import type { RequirementStatus } from '@milepost/shared';

/**
 * Display helpers.
 *
 * Dates are formatted from the `YYYY-MM-DD` string without going through `new Date()`,
 * because `new Date('2026-08-31')` is parsed as UTC midnight and then rendered in local
 * time — which shows 30 August to anyone west of Greenwich. Getting an expiry date
 * visually wrong by a day in a compliance tool is not a cosmetic bug.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** `2026-08-31` -> `31 Aug 2026`. Accepts a full ISO timestamp and uses its date part. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return '—';
  const [, y, m, d] = match;
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

/** `2026-08-31` -> `2026-08-31`, for tabular columns where alignment beats prettiness. */
export function formatDateNumeric(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(iso)}, ${d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/** "3 days", "1 day", "today". Sign is the caller's business. */
export function formatDayCount(days: number): string {
  const n = Math.abs(days);
  if (n === 0) return 'today';
  return `${n} ${n === 1 ? 'day' : 'days'}`;
}

/**
 * Turns a due-date countdown into something a human reads without doing arithmetic.
 * Overdue is stated as overdue, not as a negative number.
 */
export function formatDue(daysUntilDue: number | null): string {
  if (daysUntilDue === null) return '—';
  if (daysUntilDue < 0) return `${formatDayCount(daysUntilDue)} overdue`;
  if (daysUntilDue === 0) return 'due today';
  return `in ${formatDayCount(daysUntilDue)}`;
}

export const STATUS_LABEL: Record<RequirementStatus, string> = {
  expired: 'Lapsed',
  missing: 'Missing',
  expiring_soon: 'Expiring',
  pending_review: 'Needs review',
  satisfied: 'On file',
  not_applicable: 'Not applicable',
};

/** Sort order: whatever needs a human first. */
export const STATUS_RANK: Record<RequirementStatus, number> = {
  expired: 0,
  missing: 1,
  pending_review: 2,
  expiring_soon: 3,
  satisfied: 4,
  not_applicable: 5,
};

export const DRIVER_CLASS_LABEL: Record<string, string> = {
  cdl: 'CDL',
  clp: 'CLP',
  non_cdl: 'Non-CDL',
};

export function fullName(d: { firstName?: string; lastName?: string; first_name?: string; last_name?: string }): string {
  const first = d.firstName ?? d.first_name ?? '';
  const last = d.lastName ?? d.last_name ?? '';
  return `${first} ${last}`.trim();
}

export function formatBytes(bytes: string | number | null): string {
  if (bytes === null) return '—';
  const n = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Pluralises without the "1 drivers" bug. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}
